import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import TMISLogoW from '../img/tmis_logo_w.png';
import TMISLogoOrig from '../img/tmis_logo_orig.png';

const NAV_ITEMS = [
  { path: '/home',        label: 'Home',        icon: 'bi-house',           module: 'home' },
  // { path: '/students',    label: 'Students',    icon: 'bi-people',          module: 'students' },
  // { path: '/intakes',     label: 'Intakes',     icon: 'bi-collection',      module: 'intakes' },
  { path: '/records',     label: 'Records',     icon: 'bi-table',           module: 'records' },
  { path: '/reports',     label: 'Reports',     icon: 'bi-bar-chart',       module: 'reports' },
  { path: '/users',       label: 'Users',       icon: 'bi-person-gear',     module: 'users' },
  { path: '/permissions', label: 'Permissions', icon: 'bi-shield-check',    module: 'permissions' },
  { path: '/settings',    label: 'Settings',    icon: 'bi-gear',            module: 'settings' },
  { path: '/audit',       label: 'Audit',       icon: 'bi-journal-text',    module: 'audit' },
];

export default function Layout() {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const isDark = theme === 'dark';
  const logoSrc = isDark ? TMISLogoW : TMISLogoOrig;

  const initials = user?.name?.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() || 'U';
  const visibleItems = NAV_ITEMS.filter(item => can(item.module));

  function handleLogout() {
    logout();
    navigate('/');
  }

  const navBtnStyle = {
    background: 'transparent',
    border: '1px solid var(--nav-btn-border)',
    color: 'var(--nav-btn-color)',
    borderRadius: 6,
  };

  return (
    <>
      <nav className="navbar tmis-navbar sticky-top" style={{ padding: '0 1rem' }}>
        <div className="container-fluid px-0 d-flex align-items-center" style={{ gap: '0.75rem', minHeight: 60 }}>

          {/* ── Logo ──────────────────────────────────────── */}
          <NavLink to="/home" className="d-flex navbar-brand flex-shrink-0 mb-0 me-0 p-0">
            <img src={logoSrc} alt="TMIS" style={{ width: '4.5rem' }} />
          </NavLink>

          {/* ── Desktop nav links ─────────────────────────── */}
          <div className="d-none d-lg-flex align-items-center flex-wrap gap-0 flex-grow-1" style={{ overflowX: 'auto' }}>
            {visibleItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-link px-2 py-1 ${isActive ? 'active' : ''}`}
                style={{ whiteSpace: 'nowrap', fontSize: '0.875rem' }}
              >
                <i className={`bi ${item.icon} me-1`} />
                {item.label}
              </NavLink>
            ))}
          </div>

          {/* ── Desktop user area ─────────────────────────── */}
          <div className="d-none d-lg-flex align-items-center gap-2 ms-auto flex-shrink-0">
            <button
              onClick={toggleTheme}
              className="btn btn-sm d-flex align-items-center"
              style={{ ...navBtnStyle, padding: '4px 8px' }}
              aria-label="Toggle theme"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <i className={`bi ${isDark ? 'bi-sun-fill' : 'bi-moon-fill'}`} style={{ fontSize: '0.9rem' }} />
            </button>
            <div className="user-avatar" style={{ width: 32, height: 32, fontSize: '0.75rem', flexShrink: 0 }}>{initials}</div>
            <button
              onClick={handleLogout}
              className="btn btn-sm d-flex align-items-center gap-1"
              style={{ ...navBtnStyle, padding: '3px 10px', whiteSpace: 'nowrap' }}
            >
              <i className="bi bi-box-arrow-right" />
              <span>Sign out</span>
            </button>
          </div>

          {/* ── Tablet / iPad: avatar + hamburger ────────────────── */}
          <div className="d-none d-md-flex d-lg-none align-items-center gap-2 ms-auto">
            <div className="user-avatar" style={{ width: 36, height: 36, fontSize: '0.8rem', flexShrink: 0 }}>
              {initials}
            </div>
            <button
              className="btn btn-sm"
              style={{ ...navBtnStyle, padding: '6px 10px' }}
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Toggle navigation"
            >
              <i className={`bi ${menuOpen ? 'bi-x-lg' : 'bi-list'}`} style={{ fontSize: '1.25rem' }} />
            </button>
          </div>

          {/* ── Mobile: avatar + hamburger ────────────────── */}
          <div className="d-flex d-sm-flex d-md-none d-lg-none align-items-center gap-2 ms-auto">
            <div className="user-avatar" style={{ width: 30, height: 30, fontSize: '0.7rem', flexShrink: 0 }}>{initials}</div>
            <button
              className="btn btn-sm"
              style={{ ...navBtnStyle, padding: '4px 8px' }}
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Toggle navigation"
            >
              <i className={`bi ${menuOpen ? 'bi-x-lg' : 'bi-list'}`} style={{ fontSize: '1.1rem' }} />
            </button>
          </div>
        </div>
      </nav>

      {/* ── Mobile dropdown menu ───────────────────────────── */}
      {menuOpen && (
        <div
          className="d-lg-none"
          style={{
            position: 'fixed', top: 60, left: 0, right: 0, zIndex: 1040,
            background: 'var(--nav-mobile-bg)',
            borderBottom: '1px solid var(--nav-mobile-divider)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          }}
        >
          <div className="d-flex flex-column">
            {visibleItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `d-flex align-items-center gap-2 px-4 py-3 text-decoration-none ${isActive ? 'fw-bold' : ''}`
                }
                style={({ isActive }) => ({
                  color: isActive ? 'var(--nav-link-active)' : 'var(--nav-link)',
                  background: isActive ? 'var(--nav-link-active-bg)' : 'transparent',
                  borderLeft: isActive ? `3px solid var(--nav-mobile-active-accent)` : '3px solid transparent',
                  fontSize: '0.95rem',
                })}
              >
                <i className={`bi ${item.icon}`} style={{ width: 20, textAlign: 'center', opacity: 0.85 }} />
                {item.label}
              </NavLink>
            ))}
          </div>

          <div style={{ borderTop: '1px solid var(--nav-mobile-divider)', padding: '12px 16px' }}
            className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <div className="user-avatar" style={{ width: 34, height: 34, fontSize: '0.8rem' }}>{initials}</div>
              <div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--nav-user-name)' }}>{user?.name}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--nav-user-email)' }}>{user?.email}</div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                onClick={toggleTheme}
                className="btn btn-sm d-flex align-items-center"
                style={{ ...navBtnStyle, padding: '5px 9px' }}
                aria-label="Toggle theme"
              >
                <i className={`bi ${isDark ? 'bi-sun-fill' : 'bi-moon-fill'}`} />
              </button>
              <button
                onClick={handleLogout}
                className="btn btn-sm d-flex align-items-center gap-1"
                style={{ ...navBtnStyle, padding: '5px 10px', borderRadius: 6 }}
              >
                <i className="bi bi-box-arrow-right" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Page content ──────────────────────────────────── */}
      <div className="page-content" onClick={() => menuOpen && setMenuOpen(false)}>
        <Outlet />
      </div>
    </>
  );
}